/**
 * InvoicesPage — Tela de cobrança (domínio billing): 1 linha = 1 invoice.
 *
 * Host: billing REST API (GET /billing/invoices).
 * Ações por linha: detalhes (modal) e boleto (modal: download/cancelar).
 *
 * Contrato e Natureza vêm do TÍTULO (settlement_v2.title_debt), não da fatura — o backend resolve
 * por (operação, charge) com EXISTS correlacionado, sem view nova. Ver `charge_titles` no
 * repositório do billing e o porquê de não ser JOIN.
 *
 * TODO: juros/multa/protesto quando entrarem no boleto.
 */
import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageList from '../../templates/PageList';
import BadgeStatus from '../../atoms/BadgeStatus';
import appConfig from '../../../config/app-config';
import { FilterBuilder } from '../../molecules/FilterBuilder';
import { AppliedFilter, FilterSchema, Operator } from '../../molecules/FilterBuilder/types';
import {
    invoiceFilterSchema, serializeFilters, BUSINESS_REFERENCE_LABELS,
} from './constants/invoiceFilterSchema';
import InvoiceDetailsModal from './modals/InvoiceDetailsModal';
import BoletoModal from './modals/BoletoModal';
import InvoiceBillingEventsModal from './modals/InvoiceBillingEventsModal';
import { formatBRL } from '../../../utils/currencyUtils';
import { formatDateBR } from '../../../utils/DateUtils';
import { formatTaxId } from '../../../utils/taxIdUtils';
import { FaInfoCircle, FaBarcode, FaGavel } from 'react-icons/fa';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo'
    | 'pink' | 'olive' | 'teal' | 'gold' | 'todo' | 'turquoise';

// Mesma paleta da tela de Títulos: cobrança em aberto é gold. Antes era yellow, que nesta tela
// competia com o vermelho do vencido e não casava com o selo do mesmo título na outra tela.
const statusColors: Record<string, BadgeColor> = {
    OPEN: 'gold',
    PAID: 'green',
    CANCELLED: 'red',
    // não é gravado hoje (pagamento parcial deriva outra fatura), mas dado legado pode ter
    PARTIALLY_PAID: 'olive',
};

const scenarioColors: Record<string, BadgeColor> = {
    RECEIVABLE: 'indigo',
    INSTALLMENT: 'blue',
    GUARANTEE: 'gray',
};

const isOverdue = (row: any) =>
    row.status === 'OPEN' && row.dueDate && row.dueDate < new Date().toISOString().slice(0, 10);

// o ATable substitui a classe do cabeçalho inteira quando cssClass é informado, então a borda
// e a largura precisam vir junto — daí a constante em vez de só 'text-right'
/**
 * Coluna OCULTA, não removida: o `#` (charge) e a `Fatura` são UUIDs encurtados que não dizem nada
 * a quem opera, mas são o que o desenvolvedor usa para achar a linha no banco. Ficam no código, com
 * `display:none` no cabeçalho e na célula — descomentar a constante devolve as duas.
 */
const OCULTA = 'hidden';

const DIREITA = 'border-b text-right w-fit';

/**
 * Filtros vindos da URL. Servem a dois propósitos com um código só:
 *
 * 1. o item de menu já aponta para a tela filtrada (`?status=OPEN&collect=true`), do mesmo jeito
 *    que Operações de Crédito navega para Títulos com a operação já aplicada;
 * 2. deep-link de outra tela (uma operação, um charge, uma fatura específica).
 *
 * A leitura é dirigida pelo SCHEMA — nada de lista paralela de chaves aceitas, que sempre
 * desatualiza em relação aos filtros que a tela realmente tem.
 */
const filtersFromUrl = (params: URLSearchParams): AppliedFilter[] =>
    invoiceFilterSchema.flatMap((schema: FilterSchema) => {
        const raw = params.get(schema.key);
        if (raw == null || raw === '') return [];
        const base = { id: `url_${schema.key}`, key: schema.key, label: schema.label, type: schema.type };
        if (schema.type === 'boolean') {
            const operator: Operator = raw === 'false' ? 'is_false' : 'is_true';
            return [{ ...base, operator, value: null } as AppliedFilter];
        }
        // multivalorado: "OPEN,PAID" nos enums, "de,até" nas faixas. Sem o split, `between_dates`
        // receberia uma string e o serializador leria `value[0]` — a primeira LETRA da data.
        const multi = ['in', 'not_in', 'between', 'between_dates'].includes(schema.defaultOperator);
        return [{ ...base, operator: schema.defaultOperator, value: multi ? raw.split(',') : raw } as AppliedFilter];
    });

/**
 * Sem nada na URL, a tela abre no que interessa a quem cobra: o que está em aberto e sendo
 * cobrado. Passa pelo mesmo caminho da URL de propósito — o item de menu carrega exatamente esta
 * query string, e defini-la duas vezes é convite para as duas divergirem.
 */
export const DEFAULT_QUERY = 'status=OPEN&collect=true';

const InvoicesPage = () => {
    const [searchParams] = useSearchParams();
    const initialFilters = useMemo<AppliedFilter[]>(() => {
        const fromUrl = filtersFromUrl(searchParams);
        return fromUrl.length ? fromUrl : filtersFromUrl(new URLSearchParams(DEFAULT_QUERY));
        // searchParams é recriado a cada render; a chave estável é a query string
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.toString()]);

    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    // vazio de propósito: sem `sort` o backend aplica a ordem de leitura da tela — contrato →
    // aditamento → vencimento —, que não cabe num único campo de coluna. Mesmo arranjo da tela de
    // Títulos. Escolhendo uma coluna, o contrato passa a desempatar: as faturas de um contrato
    // continuam adjacentes em qualquer ordenação.
    const [currentSortField, setCurrentSortField] = useState('');
    const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    // semeado com o que veio da URL (ou o padrão): a primeira busca já sai filtrada
    const [activeFilters, setActiveFilters] = useState<AppliedFilter[]>(initialFilters);
    // gatilho de recarga: a consulta de situação do boleto muda a fatura por EVENTO (o biller
    // publica BOLETO_PAID/BOLETO_CANCELED depois de olhar o banco), então a lista precisa poder
    // ser relida. O `useFetch` só refaz a chamada quando a string da URL muda — daí o `_r`.
    const [refreshKey, setRefreshKey] = useState(0);
    const [detailsInvoice, setDetailsInvoice] = useState<any>(null);
    const [boletoInvoice, setBoletoInvoice] = useState<any>(null);
    const [eventsInvoice, setEventsInvoice] = useState<any>(null);

    const { address: API_URL, key: API_KEY } = appConfig.apis.billing;

    const sortParam = !currentSortField ? ''
        : currentSortOrder === 'DESC' ? `-${currentSortField}` : currentSortField;
    const params = useMemo(() => {
        const p = new URLSearchParams({
            page: String(currentPage + 1),
            size: String(currentPageSize),
        });
        if (sortParam) p.set('sort', sortParam);
        const filterParams = serializeFilters(activeFilters);
        filterParams.forEach((value, key) => p.set(key, value));
        return p;
    }, [currentPage, currentPageSize, sortParam, activeFilters]);

    const url = `${API_URL}/invoices?${params.toString()}&_r=${refreshKey}`;

    const handleFilterSearch = useCallback((filters: AppliedFilter[]) => {
        setActiveFilters(filters);
        setCurrentPage(0);
    }, []);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API_KEY || '' }
    ], [API_KEY]);

    const tableHeaders = useMemo(() => [
        // o default do ATable já é text-center; declaramos só quem foge dele
        { id: 'charge_id', title: '#', canSort: false, cssClass: OCULTA },
        { id: 'invoice_id', title: 'Fatura', canSort: false, cssClass: OCULTA },
        { id: 'contract_label', title: 'Contrato', canSort: false },
        /* Cabeçalho centralizado (o default) com conteúdo à esquerda: nome e documento formam um
           bloco de texto, e centrá-lo deixa cada linha começando num ponto diferente.

           `width: 1%` é o truque de tabela para a coluna ENCOLHER até o conteúdo. A tabela do
           ATable é `min-w-full` + `table-auto`: numa tela larga sobra largura, e o `table-auto`
           reparte a sobra proporcionalmente à largura natural de cada coluna — quase toda ela caía
           aqui, que é a única coluna de texto livre. Medido: 599px de célula para um nome de 95px,
           com todo o vazio à direita porque o conteúdo é alinhado à esquerda (nas colunas
           centralizadas a mesma sobra se divide dos dois lados e não se nota).

           Com o 1%, a coluna fica em 212px (o teto de 180px do nome + padding) e a sobra vai para
           as centralizadas. O `truncate max-w-[180px]` da célula continua sendo quem põe o teto —
           sem ele, a coluna encolheria até o NOME MAIS LONGO da página e voltaria a mandar na
           largura da tabela. */
        { id: 'payer', title: 'Pagador', canSort: false, width: '1%' },
        // Ordem padrão das telas de cobrança: Natureza, Título/NF, Nº. O documento identifica a
        // dívida e o número a posiciona dentro dela.
        { id: 'business_reference', title: 'Natureza', canSort: false },
        { id: 'document_title', title: 'Título/NF', canSort: false },
        // Nº do TÍTULO (fraciona a operação), não o da fatura, que fraciona o charge e vive em
        // details.installmentNumber.
        { id: 'title_installment_number', title: 'Nº', canSort: false },
        // Vencimento antes do Valor: a lista é varrida por PRAZO (o que vence primeiro), e o
        // valor é a consequência que se lê depois.
        { id: 'due_date', title: 'Vencimento', canSort: true, field: 'due_date' },
        { id: 'amount', title: 'Valor', canSort: true, field: 'amount', cssClass: DIREITA },
        { id: 'status', title: 'Status', canSort: true, field: 'status' },
        { id: 'collect', title: 'Em cobrança', canSort: false },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    const pageSorted = (field: string, order: 'ASC' | 'DESC') => {
        setCurrentSortField(field);
        setCurrentSortOrder(order);
    };

    const renderRows = (rows: any[]) => {
        return rows.map((row: any) => (
            // sem onClick na LINHA: a linha inteira abrindo o modal disparava no meio de uma
            // seleção de texto e no clique em qualquer célula. Quem abre detalhes é o botão.
            <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                {/* ocultas (ver OCULTA nos cabeçalhos): o desenvolvedor ainda as encontra no DOM
                    para achar a linha no banco */}
                <td className={`${OCULTA} px-4 py-2 text-sm text-center text-gray-500 font-mono`} title={row.chargeId}>
                    {row.chargeId ? row.chargeId.slice(0, 8) : '-'}
                </td>
                <td className={`${OCULTA} px-4 py-2 text-sm text-center text-gray-500 font-mono`} title={row.invoiceId}>
                    {row.invoiceId ? row.invoiceId.slice(0, 8) : '-'}
                </td>
                <td className="px-4 py-2 text-sm text-center font-medium" title={row.creditOperationId}>
                    {row.contractLabel || '-'}
                </td>
                <td className="px-4 py-2 text-sm text-left">
                    {row.payer ? (
                        <div>
                            <div className="font-medium truncate max-w-[180px]" title={row.payer.name}>{row.payer.name}</div>
                            <div className="text-xs text-gray-500">{formatTaxId(row.payer.documentNumber)}</div>
                        </div>
                    ) : '-'}
                </td>
                <td className="px-4 py-2 text-sm text-center">
                    {row.businessReference ? (
                        <BadgeStatus
                            text={BUSINESS_REFERENCE_LABELS[row.businessReference] || row.businessReference}
                            color={scenarioColors[row.businessReference] || 'gray'}
                        />
                    ) : '-'}
                </td>
                {/* vem em `details`, não no topo: é campo da PRÓPRIA fatura, e o payload separa
                    por origem — o que vem do título (Contrato, Natureza, Nº) fica no topo. Ler
                    `row.documentTitle` devolvia undefined e a coluna saía toda com "-". */}
                <td className="px-4 py-2 text-sm text-center">{row.details?.documentTitle || '-'}</td>
                <td className="px-4 py-2 text-sm text-center">{row.titleInstallmentNumber ?? '-'}</td>
                <td className={`px-4 py-2 text-sm text-center ${isOverdue(row) ? 'text-red-600 font-medium' : ''}`}>
                    {formatDateBR(row.dueDate)}
                </td>
                <td className="px-4 py-2 text-sm text-right">{formatBRL(row.amount)}</td>
                <td className="px-4 py-2 text-sm text-center">
                    <BadgeStatus text={row.statusFormatted || row.status} color={statusColors[row.status] || 'gray'} />
                </td>
                <td className="px-4 py-2 text-sm text-center">
                    {row.collect
                        ? <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded font-medium">SIM</span>
                        : <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded font-medium">NÃO</span>}
                </td>
                <td className="px-4 py-2 text-sm text-center whitespace-nowrap">
                    <button
                        className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 mr-1"
                        onClick={() => setDetailsInvoice(row)}
                        title="Detalhes"
                    ><FaInfoCircle /></button>
                    {/* Boleto SEMPRE visível, desabilitado sem boleto: escondê-lo fazia a coluna
                        mudar de largura entre linhas e obrigava a comparar duas linhas para
                        descobrir que a diferença era "não tem boleto", e não "a tela não carregou".

                        `<span title>` em volta porque um <button disabled> engole o tooltip nativo
                        em alguns navegadores — e é justamente no desabilitado que o motivo importa
                        (mesmo recurso da ChargesPage). */}
                    <span title={row.boleto ? 'Boleto (dados / download)' : 'Esta fatura não tem boleto emitido'}>
                        <button
                            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 mr-1 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed dark:disabled:bg-slate-700 dark:disabled:text-slate-500"
                            onClick={() => setBoletoInvoice(row)}
                            disabled={!row.boleto}
                            aria-label="Boleto"
                        ><FaBarcode /></button>
                    </span>
                    <button
                        className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                        onClick={() => setEventsInvoice(row)}
                        title="Eventos de cobrança (protesto, Serasa, notificação)"
                        aria-label="Eventos de cobrança"
                    ><FaGavel /></button>
                </td>
            </tr>
        ));
    };

    return (
        <>
            <PageList
                pageTitle="Cobrança — Faturas"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Cobrança', url: '#' },
                    { label: 'Cobrança — Faturas', url: '/billing/invoices' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={currentSortField}
                tableOrderSort={currentSortOrder}
                onTableSort={pageSorted}
                renderRows={renderRows}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={(p: number) => setCurrentPage(p)}
                onItemsPerPageChange={(s: number) => { setCurrentPageSize(s); setCurrentPage(0); }}
                itemsLabel="faturas"
            >
                {{
                    filterSlot: (
                        <FilterBuilder
                            schema={invoiceFilterSchema}
                            onSearch={handleFilterSearch}
                            collapsible
                            defaultCollapsed
                            title="Filtros"
                            initialFilters={initialFilters}
                        />
                    ),
                    controlSlot: (<div></div>),
                }}
            </PageList>
            {detailsInvoice && (
                <InvoiceDetailsModal
                    invoice={detailsInvoice}
                    onClose={() => setDetailsInvoice(null)}
                />
            )}
            {boletoInvoice && (
                <BoletoModal invoice={boletoInvoice} onClose={() => setBoletoInvoice(null)}
                    onChanged={() => setRefreshKey((k: number) => k + 1)} />
            )}
            {eventsInvoice && (
                <InvoiceBillingEventsModal invoice={eventsInvoice}
                    onClose={() => setEventsInvoice(null)}
                    onChanged={() => setRefreshKey((k: number) => k + 1)} />
            )}
        </>
    );
};

export default InvoicesPage;
